import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { XMarkIcon, ArrowsPointingOutIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

const NODE_CANVAS_MODE = () => 'replace';
const MAX_BOOKMARKS = 500;
const NODE_RADIUS = 5; // matches the fixed radius used in drawNode and nodeVal

// Up to 12 distinct cluster colors (hue-spaced, mid-saturation so they read on white)
const CLUSTER_PALETTE = [
  '#6366f1', // indigo
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#ec4899', // pink
  '#14b8a6', // teal
  '#a855f7', // purple
];
const UNTAGGED_COLOR  = '#9ca3af'; // gray-400
const DIMMED_COLOR    = '#e5e7eb'; // gray-200
const SELECTED_COLOR  = '#1e293b'; // slate-800 outline
const HULL_ALPHA = 0.08;

function getDomain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return ''; }
}

// Ray-casting point-in-polygon test
function pointInPolygon(px, py, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    const intersect = ((yi > py) !== (yj > py)) &&
      (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// Simple gift-wrap convex hull for an array of {x,y} points.
// Returns null when fewer than 3 points (draw nothing or a circle/line fallback).
function convexHull(points) {
  if (points.length < 3) return null;
  const pts = points.slice().sort((a, b) => a.x !== b.x ? a.x - b.x : a.y - b.y);
  const cross = (O, A, B) => (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }
  upper.pop(); lower.pop();
  return lower.concat(upper);
}

// Expand hull outward from centroid by `padding` pixels
function expandHull(hull, padding) {
  const cx = hull.reduce((s, p) => s + p.x, 0) / hull.length;
  const cy = hull.reduce((s, p) => s + p.y, 0) / hull.length;
  return hull.map(p => {
    const dx = p.x - cx, dy = p.y - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { x: p.x + (dx / len) * padding, y: p.y + (dy / len) * padding };
  });
}

const ClusterGraph = ({ bookmarks, isDark = false }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [minShared, setMinShared] = useState(2);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const engineSettledRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width));
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const truncated = useMemo(() => bookmarks.slice(0, MAX_BOOKMARKS), [bookmarks]);

  // Assign each unique tag a color index from the palette (sorted by frequency for stable assignment)
  const tagColorMap = useMemo(() => {
    const freq = new Map();
    truncated.forEach(b => (b.tags || []).forEach(t => freq.set(t, (freq.get(t) || 0) + 1)));
    const sorted = Array.from(freq.entries()).sort((a, b) => b[1] - a[1]);
    const map = new Map();
    sorted.forEach(([tag], i) => map.set(tag, CLUSTER_PALETTE[i % CLUSTER_PALETTE.length]));
    return map;
  }, [truncated]);

  // Primary tag = the most-frequently-used tag this bookmark has
  const primaryTag = useCallback(bm => {
    const tags = bm.tags || [];
    if (!tags.length) return null;
    return tags.reduce((best, t) => {
      const freq = (tagColorMap.has(t) ? [...tagColorMap.keys()].indexOf(t) : Infinity);
      const bestFreq = (tagColorMap.has(best) ? [...tagColorMap.keys()].indexOf(best) : Infinity);
      return freq < bestFreq ? t : best;
    });
  }, [tagColorMap]);

  const graphData = useMemo(() => {
    if (!truncated.length) return { nodes: [], links: [] };

    const nodes = truncated.map(b => {
      const pt = primaryTag(b);
      return {
        id: b.bookmarkId,
        title: b.title || getDomain(b.url) || b.bookmarkId,
        url: b.url || '',
        domain: getDomain(b.url || ''),
        tags: b.tags || [],
        cluster: pt,
        color: pt ? tagColorMap.get(pt) : UNTAGGED_COLOR,
      };
    });

    const links = [];
    for (let i = 0; i < truncated.length; i++) {
      const tagsI = new Set(truncated[i].tags || []);
      if (!tagsI.size) continue;
      for (let j = i + 1; j < truncated.length; j++) {
        const shared = (truncated[j].tags || []).filter(t => tagsI.has(t)).length;
        if (shared >= minShared) {
          links.push({ source: truncated[i].bookmarkId, target: truncated[j].bookmarkId, value: shared });
        }
      }
    }
    return { nodes, links };
  }, [truncated, minShared, tagColorMap, primaryTag]);

  const { connectedGraphData, isolatedNodes } = useMemo(() => {
    const connectedIds = new Set(graphData.links.flatMap(l => [
      typeof l.source === 'object' ? l.source.id : l.source,
      typeof l.target === 'object' ? l.target.id : l.target,
    ]));
    return {
      connectedGraphData: {
        nodes: graphData.nodes.filter(n => connectedIds.has(n.id)),
        links: graphData.links,
      },
      isolatedNodes: graphData.nodes.filter(n => !connectedIds.has(n.id)),
    };
  }, [graphData]);

  const { neighborSet, neighborLinks } = useMemo(() => {
    if (!selectedNode) return { neighborSet: new Set(), neighborLinks: new Set() };
    const nSet = new Set(), lSet = new Set();
    graphData.links.forEach(link => {
      const s = typeof link.source === 'object' ? link.source.id : link.source;
      const t = typeof link.target === 'object' ? link.target.id : link.target;
      if (s === selectedNode.id) { nSet.add(t); lSet.add(link); }
      if (t === selectedNode.id) { nSet.add(s); lSet.add(link); }
    });
    return { neighborSet: nSet, neighborLinks: lSet };
  }, [selectedNode, graphData.links]);

  const neighbors = useMemo(() => {
    if (!selectedNode) return [];
    const result = [];
    graphData.links.forEach(link => {
      const s = typeof link.source === 'object' ? link.source.id : link.source;
      const t = typeof link.target === 'object' ? link.target.id : link.target;
      const otherId = s === selectedNode.id ? t : t === selectedNode.id ? s : null;
      if (!otherId) return;
      const other = graphData.nodes.find(n => n.id === otherId);
      if (other) result.push({ node: other, shared: link.value });
    });
    return result.sort((a, b) => b.shared - a.shared).slice(0, 6);
  }, [selectedNode, graphData]);

  // ── Canvas draw: cluster hulls + node circles ─────────────────────────────

  // We need a stable ref to the current node positions to draw hulls in the
  // background paint callback. react-force-graph updates node x/y in-place.
  const nodesRef = useRef([]);
  useEffect(() => { nodesRef.current = connectedGraphData.nodes; }, [connectedGraphData]);

  // Stores computed hull shapes for hover hit-testing (graph-space coordinates)
  const hullsRef = useRef([]);
  const [hoveredCluster, setHoveredCluster] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const drawNode = useCallback((node, ctx, globalScale) => {
    const isDimmed = selectedNode && node.id !== selectedNode.id && !neighborSet.has(node.id);
    const isSelected = selectedNode && node.id === selectedNode.id;
    const radius = 5;

    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = isDimmed ? DIMMED_COLOR : node.color;
    ctx.fill();

    if (isSelected) {
      ctx.strokeStyle = SELECTED_COLOR;
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();
    }

    if (globalScale > 1.8 || isSelected) {
      const label = node.title.length > 28 ? node.title.slice(0, 26) + '\u2026' : node.title;
      const fontSize = Math.max(7, 10 / globalScale);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = isDark ? '#e2e8f0' : '#1e293b';
      ctx.fillText(label, node.x, node.y + radius + 2);
    }
  }, [selectedNode, neighborSet, isDark]);

  // Draw cluster hulls before nodes (background layer).
  // Also populates hullsRef with polygon data for mouse hover hit-testing.
  const drawHulls = useCallback((ctx) => {
    const nodes = nodesRef.current;
    if (!nodes.length) return;

    // Group positioned nodes by cluster
    const byCluster = new Map();
    nodes.forEach(n => {
      if (n.x == null || n.y == null) return;
      const key = n.cluster || '__untagged__';
      if (!byCluster.has(key)) byCluster.set(key, []);
      byCluster.get(key).push(n);
    });

    const newHulls = [];

    byCluster.forEach((clusterNodes, key) => {
      if (key === '__untagged__') return; // no hull for untagged
      const color = clusterNodes[0].color;
      const points = clusterNodes.map(n => ({ x: n.x, y: n.y }));
      const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
      const cy = points.reduce((s, p) => s + p.y, 0) / points.length;

      if (points.length === 1) {
        // Single node — draw a small circle
        ctx.beginPath();
        ctx.arc(points[0].x, points[0].y, 14, 0, 2 * Math.PI);
        ctx.fillStyle = color + '20';
        ctx.fill();
        // Store as approximate square polygon for hit testing
        const r = 14;
        const p = points[0];
        newHulls.push({ key, color, center: { x: cx, y: cy },
          polygon: [{ x: p.x - r, y: p.y - r }, { x: p.x + r, y: p.y - r },
                    { x: p.x + r, y: p.y + r }, { x: p.x - r, y: p.y + r }] });
      } else if (points.length === 2) {
        // Two nodes — draw a capsule
        const [a, b] = points;
        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = (-dy / len) * 14, ny = (dx / len) * 14;
        ctx.beginPath();
        ctx.moveTo(a.x + nx, a.y + ny);
        ctx.lineTo(b.x + nx, b.y + ny);
        ctx.lineTo(b.x - nx, b.y - ny);
        ctx.lineTo(a.x - nx, a.y - ny);
        ctx.closePath();
        ctx.fillStyle = color + '20';
        ctx.fill();
        newHulls.push({ key, color, center: { x: cx, y: cy },
          polygon: [{ x: a.x + nx, y: a.y + ny }, { x: b.x + nx, y: b.y + ny },
                    { x: b.x - nx, y: b.y - ny }, { x: a.x - nx, y: a.y - ny }] });
      } else {
        const hull = convexHull(points);
        if (!hull) return;
        const expanded = expandHull(hull, 16);

        ctx.beginPath();
        ctx.moveTo(expanded[0].x, expanded[0].y);
        for (let i = 1; i < expanded.length; i++) ctx.lineTo(expanded[i].x, expanded[i].y);
        ctx.closePath();
        ctx.fillStyle = color + Math.round(HULL_ALPHA * 255).toString(16).padStart(2, '0');
        ctx.fill();
        ctx.strokeStyle = color + '40';
        ctx.lineWidth = 1;
        ctx.stroke();
        newHulls.push({ key, color, center: { x: cx, y: cy }, polygon: expanded });
      }
    });

    hullsRef.current = newHulls;
  }, []);

  const getLinkColor = useCallback(link => {
    if (!selectedNode) return '#cbd5e1';
    return neighborLinks.has(link) ? '#818cf8' : '#f1f5f9';
  }, [selectedNode, neighborLinks]);

  const getLinkWidth = useCallback(link => Math.max(0.5, link.value * 0.5), []);

  const handleNodeClick = useCallback(node => {
    setSelectedNode(prev => prev?.id === node.id ? null : node);
  }, []);

  const handleBackgroundClick = useCallback(() => setSelectedNode(null), []);

  const handleEngineStop = useCallback(() => {
    engineSettledRef.current = true;
    graphRef.current?.zoomToFit(400, 40);
  }, []);

  const handleFitView = useCallback(() => {
    graphRef.current?.zoom(1, 0);
    graphRef.current?.centerAt(0, 0, 0);
    setTimeout(() => graphRef.current?.zoomToFit(400, 40), 50);
  }, []);

  const handleReheat = useCallback(() => {
    engineSettledRef.current = false;
    graphRef.current?.d3ReheatSimulation();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!graphRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    const gp = graphRef.current.screen2GraphCoords(canvasX, canvasY);

    // Don't show hull label when hovering directly over a node
    const nearNode = nodesRef.current.some(n => {
      if (n.x == null || n.y == null) return false;
      const dx = n.x - gp.x, dy = n.y - gp.y;
      return Math.sqrt(dx * dx + dy * dy) < NODE_RADIUS + 8;
    });
    if (nearNode) { setHoveredCluster(null); return; }

    let found = null;
    for (const hull of hullsRef.current) {
      if (pointInPolygon(gp.x, gp.y, hull.polygon)) { found = hull; break; }
    }
    setHoveredCluster(found ? found.key : null);
    if (found) setMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseLeave = useCallback(() => setHoveredCluster(null), []);

  useEffect(() => {
    const g = graphRef.current;
    if (!g) return;
    g.d3Force('charge')?.strength(-200);
    g.d3Force('link')?.distance(80).strength(0.4);
    g.d3ReheatSimulation();
  }, [connectedGraphData]);

  // ── Legend data: top clusters by node count ───────────────────────────────
  const clusterLegend = useMemo(() => {
    const freq = new Map();
    connectedGraphData.nodes.forEach(n => {
      if (!n.cluster) return;
      freq.set(n.cluster, (freq.get(n.cluster) || 0) + 1);
    });
    return Array.from(freq.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag, count]) => ({ tag, count, color: tagColorMap.get(tag) }));
  }, [connectedGraphData.nodes, tagColorMap]);

  if (graphData.nodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
        No bookmarks to display.
      </div>
    );
  }

  if (connectedGraphData.nodes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
        <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">No connections at this threshold.</p>
        <p className="text-sm">Try lowering the minimum shared tags slider.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookmarks.length > MAX_BOOKMARKS && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm rounded-lg px-4 py-2">
          Showing first {MAX_BOOKMARKS} of {bookmarks.length} bookmarks.
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:items-start">
        {/* Legend */}
        <div className="w-full md:w-44 md:flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Clusters</p>
          <div className="space-y-1.5">
            {clusterLegend.map(({ tag, count, color }) => (
              <div key={tag} className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">{tag}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{count}</span>
              </div>
            ))}
            {clusterLegend.length === 8 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 pt-1">+ more…</p>
            )}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 leading-relaxed">
            Color = primary tag<br />
            Hull = cluster boundary<br />
            Edge weight = shared tags
          </p>
        </div>

        {/* Graph + controls */}
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Min. shared tags:
              </label>
              <input
                type="range"
                min={1}
                max={5}
                value={minShared}
                onChange={e => setMinShared(Number(e.target.value))}
                className="w-24 accent-indigo-600"
              />
              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-400 w-4 text-center">{minShared}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleReheat}
                className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-sm transition-colors"
              >
                Re-layout
              </button>
              <button
                onClick={handleFitView}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-md shadow-sm transition-colors"
              >
                <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
                Fit to view
              </button>
            </div>
          </div>

          <div
            ref={containerRef}
            className="bg-white dark:bg-gray-900 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 relative"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {hoveredCluster && (
              <div
                className="fixed z-50 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-md shadow-lg pointer-events-none whitespace-nowrap"
                style={{ left: mousePos.x + 12, top: mousePos.y - 28 }}
              >
                {hoveredCluster}
              </div>
            )}
            <ForceGraph2D
              ref={graphRef}
              graphData={connectedGraphData}
              nodeVal={5}
              nodeColor={n => n.color}
              linkWidth={getLinkWidth}
              linkColor={getLinkColor}
              onNodeClick={handleNodeClick}
              onBackgroundClick={handleBackgroundClick}
              nodeCanvasObject={drawNode}
              nodeCanvasObjectMode={NODE_CANVAS_MODE}
              onRenderFramePre={drawHulls}
              onEngineStop={handleEngineStop}
              enableNodeDrag={true}
              warmupTicks={0}
              cooldownTime={5000}
              d3AlphaMin={0.001}
              minZoom={0.05}
              maxZoom={8}
              width={containerWidth}
              height={520}
              d3VelocityDecay={0.2}
              backgroundColor={isDark ? '#111827' : '#ffffff'}
            />
          </div>

          {selectedNode && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-4" style={{ borderColor: selectedNode.color + '80' }}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{selectedNode.title}</p>
                  {selectedNode.domain && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{selectedNode.domain}</p>
                  )}
                  {selectedNode.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedNode.tags.slice(0, 8).map(t => (
                        <span
                          key={t}
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: (tagColorMap.get(t) || UNTAGGED_COLOR) + '20',
                            color: tagColorMap.get(t) || UNTAGGED_COLOR,
                          }}
                        >
                          {t}
                        </span>
                      ))}
                      {selectedNode.tags.length > 8 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">+{selectedNode.tags.length - 8} more</span>
                      )}
                    </div>
                  )}
                  {neighbors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Similar bookmarks:</p>
                      <div className="space-y-1">
                        {neighbors.map(({ node, shared }) => (
                          <button
                            key={node.id}
                            onClick={() => setSelectedNode(node)}
                            className="w-full text-left flex items-center justify-between gap-2 text-xs text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 py-0.5"
                          >
                            <span className="truncate">{node.title}</span>
                            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{shared} tag{shared !== 1 ? 's' : ''}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedNode.url && (
                    <a
                      href={selectedNode.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-sm font-medium transition-colors"
                      style={{ color: selectedNode.color }}
                    >
                      Open <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="ml-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isolatedNodes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
            No connections at this threshold
          </p>
          <div className="flex flex-wrap gap-2">
            {isolatedNodes.map(node => (
              <a
                key={node.id}
                href={node.url}
                target="_blank"
                rel="noopener noreferrer"
                title={node.title}
                className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-400 px-3 py-1 rounded-full text-sm transition-colors max-w-xs truncate"
              >
                {node.domain || node.title}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClusterGraph;
